import React, { FC, useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Button from '../../../components/Library/Button';
import Input from '../../../components/Library/Input';
import Label from '../../../components/Library/Label';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '../../../components/Library/Modal';
import Select from '../../../components/Library/Select';
import InlineLoadingSpinner from '../../../components/LoadingSpinner/InlineLoadingSpinner';
import { formatToLocalTime, humanizedTimeSpan } from '../../../libs/libDate';
import { snakeCaseToWords } from '../../../libs/libString';
import { sortBy } from '../../../libs/utils';
import {
  ScimIdp, ScimIntegration, ScimIntegrationWithToken, useSCIM_IntegrationsApi
} from '../../../middle_auth_api';
import { ScimIdpEnum } from '../../../types/ScimIdpEnum';

interface ScimIntegrationsDetailsBodyProps {
  integration: ScimIntegration | ScimIntegrationWithToken;
  rekeyIntegration: () => void;
  editMode: boolean;
  registerSave: (saveCallback:  () => Promise<ScimIntegration>) => void;
  unregisterSave: () => void;
}

const ScimIntegrationsDetailsBody: React.FC<ScimIntegrationsDetailsBodyProps> = (props) => {
  const scimIntegrationsApi = useSCIM_IntegrationsApi();
  const queryClient = useQueryClient();
  
  const [name, setName] = useState(props.integration.name);
  const [enabled, setEnabled] = useState(props.integration.enabled);
  const [idpHint, setIdpHint] = useState(props.integration.idp_hint);
  const [idpOtherName, setIdpOtherName] = useState(props.integration.idp_other_name as string ?? '');

  const save = async (): Promise<ScimIntegration> => {
    const integration = await scimIntegrationsApi.put_scim_integration_by_id({
      name,
      enabled,
      idp_hint: idpHint,
      idp_other_name: idpHint === 'other' ? idpOtherName : null
    }, {
      params: {
        id: props.integration.id
      }
    });

    return integration;
  };

  const revokeToken = (tokenId: string) => {
    return () => {
      void (async () => {
        await scimIntegrationsApi.delete_scim_integration_token_by_id(undefined, {
          params: {
            integration_id: props.integration.id,
            token_id: tokenId
          }
        });

        queryClient.invalidateQueries(['scim-integration', props.integration.id])
      })();
    }
  }

  useEffect(() => {
    props.registerSave(save);
    return () => {
      props.unregisterSave();
    }
  }, []);

  useEffect(() => {
    if (!props.editMode) {
      setName(props.integration.name);
      setEnabled(props.integration.enabled);
      setIdpHint(props.integration.idp_hint);
      setIdpOtherName(props.integration.idp_other_name as string ?? '');
    }
  }, [props.editMode])

  return <>
    <div className='p-4'>
      <div>
        <Label htmlFor='name'>Name</Label>
        <Input id='name' readOnly={!props.editMode} value={name} onChange={(e) => setName(e.target.value)} className='w-full' />
      </div>
      <div>
        <Label htmlFor='enabled'>Enabled</Label>
        <Input id='enabled' disabled={!props.editMode} type='checkbox' checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
      </div>
      <div>
        <Label htmlFor='idpHint'>IdP</Label>
        <Select id='idpHint' disabled={!props.editMode} className='block w-full' value={idpHint} onChange={(e) => setIdpHint(e.target.value as ScimIdp)}>
          <option value={''} disabled>Choose Your IdP</option>
          {Object.keys(ScimIdpEnum).map((idp) => (
            <option key={idp} value={idp}>{snakeCaseToWords(idp)}</option>
          ))}
        </Select>
      </div>
      <div>
        <Label htmlFor='idpOtherName'>IdP Name</Label>
        <Input id='idpOtherName' readOnly={!props.editMode} disabled={idpHint != "other"} value={idpOtherName} onChange={(e) => setIdpOtherName(e.target.value)} className='w-full' />
      </div>
      <div>
        <Label htmlFor='token'>Token</Label>
        <div className='w-full'>
          <div className='w-[calc(100%-40px)] inline-block'>
            {((props.integration as ScimIntegrationWithToken).token != null)
              ? <Input id='token' type='text' readOnly value={(props.integration as ScimIntegrationWithToken).token} className='inline-block' />
              : <Input id='token' type='password' disabled value={'This is a fake secret. Do not use.'} className='inline-block' />}
          </div>
          <Button variant='danger' onClick={props.rekeyIntegration} title='Generate a new key. WARNING: This will set the current token to expire in 24 hours.'><i className="bi bi-recycle"></i></Button>
        </div>
      </div>
      <div className='py-3'>
        <h3 className='text-xl'>Active Tokens:</h3>
      </div>
      {props.integration.tokens.sort(sortBy('created_timestamp', true)).map((token) => <div key={token.id} className='w-full mb-2 border'>
        <div className='flex flex-row'>
          <table className='w-[calc(100%-40px)] inline-block'>
            <tbody>
              <tr>
                <th className='text-left mr-2'>Token</th>
                <td>{token.id}</td>
              </tr>
              <tr>
                <th className='text-left mr-2'>Created</th>
                <td>{humanizedTimeSpan(token.created_timestamp)} <small>({formatToLocalTime(token.created_timestamp)})</small></td>
              </tr>
              <tr>
                <th className='text-left mr-2'>Expires</th>
                <td>{token.revoked_timestamp == null ? <i>Never</i> : <>{humanizedTimeSpan(token.revoked_timestamp as string)} <small>({formatToLocalTime(token.revoked_timestamp as string)})</small></>}</td>
              </tr>
              <tr>
                <th className='text-left mr-2'>Last Used</th>
                <td>{token.last_use_timestamp == null ? <i>Never</i> : <>{humanizedTimeSpan(token.last_use_timestamp as string)} <small>({formatToLocalTime(token.last_use_timestamp as string)})</small></>}</td>
              </tr>
            </tbody>
          </table>
          <Button variant='danger' onClick={revokeToken(token.id)} title='Immediately revokes this token.'><i className="bi bi-trash"></i></Button>
        </div>
      </div>)}
    </div>
  </>;
}

const ScimIntegrationsDetails: React.FC = () => {
  const { integrationId } = useParams();
  const navigate = useNavigate();
  const scimIntegrationsApi = useSCIM_IntegrationsApi();
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-extra-parens
  const [saveCallback, setSaveCallback] = useState<null | (() => Promise<ScimIntegration>)>(null);
  const [rekeyedIntegration, setRekeyedIntegration] = useState<ScimIntegrationWithToken | null>(null);

  const rekeyMutation = useMutation(async () => {
    const integration = scimIntegrationsApi.rekey_scim_integration_by_id(undefined, {
      params: {
        id: integrationId!
      }
    });

    return integration;
  }, {
    onSuccess: (data) => {
      setRekeyedIntegration(data);
    }
  })

  const integrationQuery = useQuery(['scim-integration', integrationId], async () => {
    const integration = await scimIntegrationsApi.get_scim_integration_by_id({
      params: {
        id: integrationId!
      }
    });

    return integration;
  });

  const updateMutation = useMutation(async () => {
    setRekeyedIntegration(null);
    if (saveCallback != null) {
      return await saveCallback();
    }
    throw Error('No Save Callback Registered');
  }, {
    onSuccess: (data) => {
      queryClient.setQueryData(['scim-integration', integrationId], data);
    }
  })



  const closeModal = () => {
    navigate('..');
  };

  const optionButton = <>
    <button className='box-content ml-auto mr-2 w-4 h-4 text-nav-txt border-none rounded-none focus:shadow-none focus:outline-none hover:text-nav-text-dark hover:opacity-75 hover:no-underline' onClick={() => setEditMode(true)}><i className='bi bi-pencil-square'></i></button>
  </>;

  return <>
    <Modal show={true} closeModal={closeModal}>
      <ModalHeader closeModal={closeModal} optionButtons={editMode ? undefined : optionButton}>{integrationQuery.data?.name ?? 'Loading...'}</ModalHeader>
      <ModalBody>
        {(integrationQuery.isLoading || rekeyMutation.isLoading || updateMutation.isLoading) ?
          <InlineLoadingSpinner />
        :
        <ScimIntegrationsDetailsBody editMode={editMode} integration={rekeyedIntegration ?? integrationQuery.data!} rekeyIntegration={rekeyMutation.mutate} registerSave={setSaveCallback} unregisterSave={() => setSaveCallback(null)} />}
      </ModalBody>
      <ModalFooter>
        {editMode
          ? <>
        <Button variant='primary' type='submit' onClick={() => updateMutation.mutate()}>Save</Button>
        <Button variant='secondary' type='reset' onClick={() => setEditMode(false)}>Cancel</Button>
        </>
          : <>
          {integrationQuery.data != null && <Link to='delete' className='mx-auto px-2 py-1 rounded-lg text-danger-text bg-danger hover:bg-danger-dark shadow-md shadow-hover'>Delete</Link>}
        </>}
      </ModalFooter>
    </Modal>
    <Outlet context={integrationQuery.data} />
  </>;
}

export default ScimIntegrationsDetails;